import { useState, useEffect } from 'react';
import { PLANBASE_CONFIG } from '../config';

export interface Project {
  id: string;
  slug: string;
  data: {
    title: string;
    description: string;
    category?: string;
    style?: string;
    location?: string;
    year?: string;
    architect?: string;
    images?: string[];
    coverImage?: string;
    tags?: string[];
    isLocked?: boolean;
  };
  status: 'draft' | 'published' | 'archived';
  created_at: string;
  updated_at: string;
}

interface UseProjectsOptions {
  category?: string;
  style?: string;
  status?: 'draft' | 'published' | 'archived';
  limit?: number;
  offset?: number;
}

interface UseProjectsResult {
  projects: Project[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  total: number;
}

export function useProjects(options: UseProjectsOptions = {}): UseProjectsResult {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);

      const url = new URL(`${PLANBASE_CONFIG.apiUrl}/sdk/cms/records`);
      url.searchParams.set('project_id', PLANBASE_CONFIG.projectId);
      url.searchParams.set('database_slug', 'projects');
      
      if (options.status) {
        url.searchParams.set('status', options.status);
      } else {
        url.searchParams.set('status', 'published');
      }
      
      if (options.limit) {
        url.searchParams.set('limit', options.limit.toString());
      }
      
      if (options.offset) {
        url.searchParams.set('offset', options.offset.toString());
      }

      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
          'X-Project-ID': PLANBASE_CONFIG.projectId,
        },
      });

      if (response.ok) {
        const data = await response.json();
        let fetchedProjects = data.records || [];
        
        // Client-side filtering for category and style
        if (options.category) {
          fetchedProjects = fetchedProjects.filter(
            (p: Project) => p.data.category === options.category
          );
        }
        
        if (options.style) {
          fetchedProjects = fetchedProjects.filter(
            (p: Project) => p.data.style === options.style
          );
        }
        
        setProjects(fetchedProjects);
        setTotal(data.total || fetchedProjects.length);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch projects');
      }
    } catch (err) {
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [options.category, options.style, options.status, options.limit, options.offset]);

  return {
    projects,
    loading,
    error,
    refetch: fetchProjects,
    total,
  };
}
